/*
* Froala editor init
* */
import {componentsHelper} from "../helpers/componentsHelper.js";
import {autoSave} from "../common/autoSave.js";

const keySFR = "lSA3D-17C2D1B1E1D1B1C2sGXd1WWTDSGXYOUKh1KINLb1OG1g1H-8D1B3D3D1E6D1B1B4E4F3==";
const colorsArray = [
    '#61BD6D', '#1ABC9C', '#54ACD2', '#2C82C9', '#9365B8', '#475577', '#CCCCCC',
    '#41A85F', '#00A885', '#3D8EB9', '#2969B0', '#553982', '#28324E', '#000000',
    '#F7DA64', '#FBA026', '#EB6B56', '#E25041', '#A38F84', '#EFEFEF', '#FFFFFF',
    '#FAC51C', '#F37934', '#D14841', '#B8312F', '#7C706B', '#D1D5D8', 'REMOVE'
];
const imageSettingsArray = {
    imageEditButtons: ['imageReplace', 'imageAlign', 'imageDisplay', 'imageAlt', 'imageSize', 'imageRemove'],
    imageResize: true,
    imageResizeWithPercent: true,
    imageAllowedTypes: ['jpeg', 'jpg', 'png', 'gif', 'svg+xml'],
    imageDefaultAlign: 'center',
    imageDefaultDisplay: 'block',
    imageDefaultWidth: 0,
    imageDefaultHeight: 0,
}


// Colors the account added on top of the stock palette: the brand color from
// Settings > Branding (rendered by the layout as data-theme-color) plus the
// HEX codes the user typed by hand recently (kept in this browser only).
const RECENT_COLORS_KEY = 'cg_editor_recent_colors';
const RECENT_COLORS_MAX = 5;

const normalizeHex = (value) => {
    const hex = String(value || '').trim().toUpperCase();
    return /^#[0-9A-F]{6}$/.test(hex) ? hex : null;
};

const getAccountThemeColor = () => normalizeHex(document.body.dataset.themeColor);

const getRecentColors = () => {
    try {
        const stored = JSON.parse(localStorage.getItem(RECENT_COLORS_KEY)) || [];
        return stored.map(normalizeHex).filter(Boolean);
    } catch (e) {
        return [];
    }
};

const rememberColor = (value) => {
    const hex = normalizeHex(value);
    if (!hex || hex === getAccountThemeColor() || colorsArray.includes(hex)) {
        return;
    }
    const recent = [hex, ...getRecentColors().filter((color) => color !== hex)].slice(0, RECENT_COLORS_MAX);
    try {
        localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(recent));
    } catch (e) {
        // storage unavailable (private mode, quota) — the palette just stays stock
    }
};

const getCustomColors = () => {
    const theme = getAccountThemeColor();
    return theme ? [theme, ...getRecentColors()] : getRecentColors();
};

// Same markup Froala uses for its own tiles, so clicks go through its applyXColor command.
const customColorTile = (type, hex) => `<span class="fr-command fr-select-color cg-custom-color" style="background:${hex};"
    tabIndex="-1" aria-selected="false" role="button" data-cmd="apply${type}Color" data-param1="${hex}" title="${hex}">
    <span class="fr-sr-only"> ${hex} </span></span>`;

// Prepend the custom colors as the first row of the picker each time it opens.
const injectCustomColors = (editor, type) => {
    const popup = editor.popups.get(`${type}Color.picker`);
    const set = popup && popup.get(0).querySelector(`.fr-color-set.fr-${type}-color`);
    if (!set) {
        return;
    }
    set.querySelectorAll('.cg-custom-color, .cg-custom-color-break').forEach((el) => el.remove());
    const colors = getCustomColors();
    if (colors.length) {
        set.insertAdjacentHTML('afterbegin', `${colors.map((hex) => customColorTile(type, hex)).join('')}<br class="cg-custom-color-break">`);
    }
};

const colorPickerEvents = {
    'popups.show.textColor.picker': function () {
        injectCustomColors(this, 'text');
    },
    'popups.show.backgroundColor.picker': function () {
        injectCustomColors(this, 'background');
    },
    'commands.before': function (cmd) {
        if (cmd === 'customTextColor' || cmd === 'customBackgroundColor') {
            const type = cmd === 'customTextColor' ? 'text' : 'background';
            const popup = this.popups.get(`${type}Color.picker`);
            rememberColor(popup && popup.find('.fr-color-hex-layer input').val());
        }
    },
};

export const FroalaInit = () => {

    let editorInstances = [];

    const commonOptions = {
        key: keySFR,
        toolbarInline: true,
        charCounterCount: false,
        attribution: false,
        pasteDeniedTags: ['table', 'td', 'tr', 'img'],
        pasteDeniedAttrs: ['style', 'class', 'id', 'dir'],
        pastePlain: true,
        removeInlineStylesOnPaste: true,
        events: {
            'paste.beforeCleanup': clipboardHtml => clipboardHtml.replace(/<!DOCTYPE[^>]*>/gi, ''),
            'focus': hideToolbar,
            'contentChanged': autoSaveDebounced,
            'paste.after': cleanupPaste,
            'keydown': handleKeydown,
            'toolbar.show': function (e, editor) {}
        }
    };

    const instances = [
        {
            selector: '.table-container',
            options: {
                placeholderText: '',
                enter: false,
                toolbarButtons: {
                    moreText: {
                        buttons: ['bold', 'italic', 'underline', 'textColor', 'insertImage', 'insertLink'],
                        buttonsVisible: 6
                    },
                },
                pluginsEnabled: ['table', 'image'],
                tableResizerOffset: 10,
                tableStyles: {
                    'fr-dashed-borders': 'Dashed Borders',
                    'fr-alternate-rows': 'Alternate Rows'
                },
                imageEditButtons: ['imageReplace', 'imageAlign', 'imageDisplay', 'imageAlt', 'imageSize', 'imageRemove'],
                tableInsertHelper: false,
                tableEditButtons: ['insertImage', 'tableHeader', 'tableRows', 'tableColumns', 'tableCells', '-', 'tableCellBackground', 'tableCellVerticalAlign', 'tableCellHorizontalAlign'],
                colorsText: colorsArray,
                colorsBackground: colorsArray,
                tableColors: colorsArray,
                tableCellMultipleStyles: false,
                imageResize: true,
                imageResizeWithPercent: true
            }
        },
        {
            selector: '.fr-editor-heading-h2',
            options: {
                placeholderText: 'Enter a heading',
                toolbarVisibleWithoutSelection: false,
                wordCounterCount: false,
                htmlAllowedTags: [],
                toolbarButtons: [],
                quickInsertButtons: [],
                pluginsEnabled: false,
                excludeTags: ['TABLE', 'TD', 'TR', 'img']
            }
        },
        {
            selector: '.fr-editor-paragraph',
            options: {
                linkAlwaysBlank: true,
                linkList: false,
                linkStyles: false,
                listAdvancedTypes: false,
                colorsText: colorsArray,
                colorsBackground: colorsArray,
                toolbarButtons: [
                    ['paragraphFormat', 'bold', 'italic', 'underline', 'strikeThrough', '|',  'colorsButtons', 'textColor', 'backgroundColor', 'clearFormatting'],
                    '-',
                    ['insertLink', '|', 'align', 'formatOL', 'formatUL', '|', 'indent', 'outdent' ],
                ],
                toolbarVisibleWithoutSelection: true,
                excludeTags: ['TABLE', 'TD', 'TR', 'img'],
                paragraphFormatSelection: true,
                paragraphMultipleStyles: true,
                paragraphFormat: {
                    N: 'Normal',
                    H1: 'Heading 1',
                    H2: 'Heading 2',
                    H3: 'Heading 3',
                    H4: 'Heading 4',
                    H5: 'Heading 5',
                },
                pluginsEnabled: ['align', 'colorsButtons', 'colors', 'draggable', 'fontFamily', 'fontSize', 'inlineStyle', 'lineBreaker', 'link', 'lists', 'paragraphFormat', 'paragraphStyle', 'quote', 'url', 'image'],
                shortcutsEnabled: ['show', 'bold', 'italic', 'underline', 'strikeThrough', 'indent', 'outdent', 'undo', 'redo', 'clearFormatting'],
                tabSpaces: 4,
                events: {
                    'toolbar.show': function (e, editor) {}
                }
            }
        },
        {
            selector: '.fr-editor-tabs',
            options: {
                linkAlwaysBlank: true,
                attribution: false,
                toolbarInline: true,
                charCounterCount: false,
                linkList: false,
                linkStyles: false,
                listAdvancedTypes: false,
                colorsText: colorsArray,
                colorsBackground: colorsArray,
                imageEditButtons: ['imageReplace', 'imageAlign', 'imageDisplay', 'imageAlt', 'imageSize', 'imageRemove'],
                imageResize: true,
                imageResizeWithPercent: true,
                imageAllowedTypes: ['jpeg', 'jpg', 'png', 'gif', 'svg+xml'],
                imageDefaultAlign: 'center',
                imageDefaultDisplay: 'block',
                imageDefaultWidth: 0,
                imageDefaultHeight: 0,
                toolbarVisibleWithoutSelection: true,
                toolbarButtons: [
                    ['paragraphFormat', 'bold', 'italic', 'underline', 'strikeThrough', '|',  'colorsButtons', 'textColor', 'backgroundColor', 'clearFormatting', 'insertImage'],
                    '-',
                    ['insertLink','|', 'align', 'formatOL', 'formatUL','|', 'indent', 'outdent' ],
                ],
                excludeTags: ['TABLE', 'TD', 'TR'],
                pasteDeniedTags: ['table', 'td', 'tr'],
                pasteDeniedAttrs: ['style', 'class', 'id', 'dir'],
                paragraphFormatSelection: true,
                paragraphMultipleStyles: true,
                paragraphFormat: {
                    N: 'Normal',
                    H1: 'Heading 1',
                    H2: 'Heading 2',
                    H3: 'Heading 3',
                    H4: 'Heading 4',
                    H5: 'Heading 5',
                },
                pluginsEnabled: ['align', 'colorsButtons', 'colors', 'draggable', 'fontFamily', 'fontSize', 'inlineStyle', 'lineBreaker', 'link', 'lists', 'paragraphFormat', 'paragraphStyle', 'quote', 'url', 'image'],
                pastePlain: true,
                removeInlineStylesOnPaste: true,
                shortcutsEnabled: ['show', 'bold', 'italic', 'underline', 'strikeThrough', 'indent', 'outdent', 'undo', 'redo', 'clearFormatting'],
                tabSpaces: 4,
                events: {
                    'focus': function () {
                        hideToolbar();
                    },
                    'contentChanged': function () {
                        let typingTimer;
                        clearTimeout(typingTimer);
                        typingTimer = setTimeout(() => {
                            autoSave.init(null, 500)
                        }, 1000);
                    },
                    "image.beforeUpload": function (files) {
                        let editor = this;
                        if (files.length) {
                            // Create a File Reader.
                            let reader = new FileReader();
                            // Set the reader to insert images when they are loaded.
                            reader.onload = function (e) {
                                let result = e.target.result;
                                editor.image.insert(result, null, null, editor.image.get());
                            };
                            // Read image as base64.
                            reader.readAsDataURL(files[0]);
                        }
                        editor.popups.hideAll();
                        // Stop default upload chain.
                        return false;
                    },
                    'image.inserted': function (response) {
                        // get section id
                        let section_id = response[0].closest('.editor-section').id;
                        // update image src

                        // set unique id for image
                        let asset_id = componentsHelper.setAssetUniqueID(null, '1', '1', 'image');
                        let img = response[0];
                        // optimize image on upload using helper function
                        componentsHelper.optimizeImage(img, img.src);
                        // set data-asset-id for image
                        response[0].setAttribute('data-asset-id', asset_id);

                    },
                    'paste.after': function (e) {
                        const paragraphs = this.el.querySelectorAll('p');
                        for (let i = 0; i < paragraphs.length; i++) {
                            if (!paragraphs[i].textContent.trim()) {
                                paragraphs[i].remove();
                            }
                        }
                        setTimeout(() => {
                            let isPasted = this.el.querySelectorAll('#isPasted');
                            if (isPasted) {
                                isPasted.forEach((el) => {
                                    el.removeAttribute('id');
                                });
                            }
                        }, 100)

                    },
                    'paste.beforeCleanup': function (clipboardHtml) {
                        clipboardHtml = clipboardHtml.replace(/<!DOCTYPE[^>]*>/gi, '');

                        return replaceQuotesOnPaste(clipboardHtml);
                    },
                    'keydown': function (e) {
                        replaceQuotes(e, this);
                    },
                    'commands.after': function (cmd) {
                        //console.log(cmd)
                        switch (cmd) {
                            case 'align':
                                froalaClassnameReplacingAlign(this.selection.element());
                                break;
                            case 'indent':
                                froalaClassnameReplacingIndent(this.selection.element(), true);
                                break;
                            case 'outdent':
                                froalaClassnameReplacingIndent(this.selection.element(), false);
                                break;
                            default:
                                break;
                        }
                    }
                }
            }
        }, // end of '.fr-editor-tabs'
        {
            selector: '.fr-editor-list',
            options: {
                linkAlwaysBlank: true,
                attribution: false,
                toolbarInline: true,
                charCounterCount: false,
                linkList: false,
                linkStyles: false,
                listAdvancedTypes: false,
                colorsText: colorsArray,
                colorsBackground: colorsArray,
                imageResize: true,
                imageResizeWithPercent: true,
                imageEditButtons: ['imageReplace', 'imageAlign', 'imageDisplay', 'imageAlt', 'imageSize', 'imageRemove'],
                toolbarButtons: [
                    ['insertImage', 'bold', 'italic', 'underline', 'strikeThrough', '|',  'colorsButtons', 'textColor', 'backgroundColor', 'clearFormatting'],
                    '-',
                    ['insertLink', '|', 'indent', 'outdent' ],
                ],
                toolbarVisibleWithoutSelection: false,
                pluginsEnabled: ['lists', 'image'],
                // plugins: {
                //     lists: {
                //         listAdvancedTypes: true
                //     }
                // },
                multiLine: false,
                excludeTags: ['TABLE', 'TD', 'TR', 'p'],
                pasteDeniedTags: ['table', 'td', 'tr', 'p'],
                pasteDeniedAttrs: ['style', 'class', 'id'],
                pastePlain: true,
                removeInlineStylesOnPaste: true,
                events: {
                    'focus': function () {
                        hideToolbar();
                    },
                    "image.beforeUpload": function(files) {
                        let editor = this;
                        if (files.length) {
                            // Create a File Reader.
                            let reader = new FileReader();
                            // Set the reader to insert images when they are loaded.
                            reader.onload = function(e) {
                                let result = e.target.result;

                                editor.image.insert(result, null, null, editor.image.get());
                            };
                            // Read image as base64.
                            reader.readAsDataURL(files[0]);
                        }
                        editor.popups.hideAll();
                        // Stop default upload chain.
                        return false;
                    },
                    'image.inserted': function (response) {
                        // get section id
                        let section_id = response[0].closest('.editor-section').id;
                        // update image src

                        // set unique id for image
                        let asset_id = componentsHelper.setAssetUniqueID(null, '1', '1', 'image');
                        let img = response[0];
                        // optimize image on upload using helper function
                        componentsHelper.optimizeImage(img, img.src);
                        // set data-asset-id for image
                        response[0].setAttribute('data-asset-id', asset_id);

                    },
                    'paste.after': function () {
                        const paragraph = this.el.querySelectorAll('p');
                        for (let j = 0; j < paragraph.length; j++) {
                            paragraph[j].outerHTML = `<li>${paragraph[j].innerHTML}</li>`;
                        }
                        // if li has inner li without ul/ol, wrap it with ul
                        const li = this.el.querySelectorAll('li');
                        for (let i = 0; i < li.length; i++) {
                            if (li[i].querySelector('li') && !li[i].querySelector('ul') && !li[i].querySelector('ol')) {
                                li[i].innerHTML = `<ul>${li[i].innerHTML}</ul>`;
                            }
                        }
                    },
                    'paste.beforeCleanup': function (clipboardHtml) {
                        clipboardHtml = clipboardHtml.replace(/<!DOCTYPE[^>]*>/gi, '');
                        return replaceQuotesOnPaste(clipboardHtml);
                    },
                    'contentChanged': function () {
                        if (this.html.get().trim() === '') {
                            setTimeout(() => {
                                if (this.el.closest('.editor-section').dataset.layout === 'listUl') {
                                    this.html.set('<ul><li>\u00a0</li></ul>');
                                } if (this.el.closest('.editor-section').dataset.layout === 'listCheckbox') {
                                    this.html.set('<ul><li>\u00a0</li></ul>');
                                } else {
                                    this.html.set('<ol><li>\u00a0</li></ol>');
                                }
                            },10);
                            setTimeout(() => {
                                this.selection.setAtEnd(this.el.querySelector('li'));
                                this.selection.restore();
                            },30);

                        }


                        setTimeout(() => {
                            // on start typing if not list add list, and add typed text to li
                            let typedElement = this.el;
                            let typed = this.el.textContent.trim();
                            // console.log(typedElement)
                            //of typedElement not contain ul or ol, add ul or ol and li and paste typed text onto it
                            if (!typedElement.querySelector('ul') && !typedElement.querySelector('ol')) {
                                if(this.el){
                                    if (this.el.closest('.editor-section').dataset.layout === 'listUl') {
                                        this.html.set('<ul><li>'+typed+'</li></ul>');
                                    } else {
                                        this.html.set('<ol><li>'+typed+'</li></ol>');
                                    }
                                    this.selection.setAtEnd(this.el.querySelector('li'));
                                    this.selection.restore();
                                }

                            }
                        }, 30);

                        let typingTimer;
                        clearTimeout(typingTimer);
                        typingTimer = setTimeout(() => {
                            autoSave.init(null, 500)
                        }, 1000);
                    },
                    'keydown': function (e) {
                        // if shift + enter pressed in list add br
                        if (e.which === 13 && e.shiftKey) {
                            this.html.insert('<br>');
                            this.selection.restore();
                            e.preventDefault();
                            return false;
                        }
                        if (e.which === 13) {
                            //console.log(this.selection.element());
                            // create new list item after current list item
                            if (this.selection.element().closest('li')) {
                                let newLi = document.createElement('li');

                                let parentElement = this.selection.element().closest('ul') ? this.selection.element().closest('ul') : this.selection.element().closest('ol'); // replace with the selector of your parent element
                                if(parentElement.classList.contains('checkbox-list')) {
                                    newLi.innerHTML = `<label class="checkbox-element" contenteditable="false"><input type="checkbox"><span class="checkbox-icon" contenteditable="false">&nbsp;</span>&nbsp;</label> \u00a0`;
                                } else {
                                    newLi.textContent = '\u00a0';
                                }
                                if(parentElement === null) {
                                    parentElement = this.selection.element().closest('ol');
                                }

                                parentElement.insertBefore(newLi, this.selection.element().nextSibling);

                                // Check if we have checkbox inside LI then set cursor after it
                                if(this.selection.element().querySelector('.checkbox-element')) {
                                    this.selection.setAtEnd(newLi);
                                } else {
                                    this.selection.setAfter(this.selection.element());
                                }

                                this.selection.restore();
                                e.preventDefault();
                                return false;
                            }

                        }

                        if (e.which === 9 && e.shiftKey) { // Shift+Tab pressed
                            const paragraph = this.el.querySelectorAll('p');
                            for (let j = 0; j < paragraph.length; j++) {
                                paragraph[j].outerHTML = `<li>${paragraph[j].innerHTML}</li>`;
                            }
                        }

                        // on click to delete or backspace key press, if li is empty remove it
                        if (e.which === 8 || e.which === 46) {
                            const li = this.el.querySelectorAll('li');
                            for (let i = 0; i < li.length; i++) {
                                if (!li[i].textContent.trim()) {
                                    li[i].remove();
                                }
                            }
                        }
                        replaceQuotes(e, this);
                    }
                }
            }
        }, // end of '.fr-editor-list'
        {
            selector: '.fr-editor-statement',
            options: {
                linkAlwaysBlank: true,
                attribution: false,
                toolbarInline: true,
                charCounterCount: false,
                linkList: false,
                linkStyles: false,
                listAdvancedTypes: false,
                colorsText: colorsArray,
                toolbarButtons: ['bold', 'italic', 'underline', 'strikeThrough', 'textColor', 'insertLink'],
                toolbarVisibleWithoutSelection: false,
                excludeTags: ['TABLE', 'TD', 'TR', 'img', 'ul', 'ol'],
                pasteDeniedTags: ['table', 'td', 'tr', 'img', 'ul', 'ol'],
                pasteDeniedAttrs: ['style', 'class', 'id'],
                pastePlain: true,
                removeInlineStylesOnPaste: true,
                events: {
                    'paste.beforeCleanup': function (clipboard_html) {
                        // Use a regular expression to remove doctype from the pasted content
                        return clipboard_html.replace(/<!DOCTYPE[^>]*>/gi, '');
                    },
                    'focus': function () {
                        hideToolbar();
                    },
                    'paste.after': function () {
                        const lists = this.el.querySelectorAll('ul, ol');
                        // find all lists and remove tags ul ol
                        for (let i = 0; i < lists.length; i++) {
                            lists[i].outerHTML = lists[i].innerHTML;
                        }

                        const li = this.el.querySelectorAll('li');
                        for (let j = 0; j < li.length; j++) {
                            li[j].outerHTML = `<p>${li[j].innerHTML}</p>`;
                        }
                    },
                    'contentChanged': function () {
                        let typingTimer;
                        clearTimeout(typingTimer);
                        typingTimer = setTimeout(() => {
                            autoSave.init(null, 500)
                        }, 1000);
                    },
                }
            }
        }
    ];


    function autoSaveDebounced() {
        let typingTimer;
        clearTimeout(typingTimer);
        typingTimer = setTimeout(() => {
            autoSave.init(null, 500)
        }, 1000);
    }

    function cleanupPaste(e) {
        const paragraphs = this.el.querySelectorAll('p');
        for (let i = 0; i < paragraphs.length; i++) {
            if (!paragraphs[i].textContent.trim()) {
                paragraphs[i].remove();
            }
        }
    }

    function handleKeydown(e) {
        replaceQuotes(e, this);
    }

    instances.forEach(({selector, options}) => {
        // options.events fully replaces commonOptions.events (kept as is); the picker hooks go on top of either
        const events = {...(options.events || commonOptions.events), ...colorPickerEvents};
        const editor = new FroalaEditor(selector, {...commonOptions, ...options, events});
        editorInstances.push(editor);
    });
};

const froalaClassnameReplacingAlign = (element) => {
    console.log(element)
    if (element.style.textAlign) {
        // Convert the inline style to a class
        const alignment = element.style.textAlign;
        // if classname has ta_ in it remove it
        Array.from(element.classList).forEach(className => {
            if (className.includes('ta_')) {
                element.classList.remove(className);
            }
        });
        // add the new class
        element.classList.add(`ta_${alignment}`);
        element.style.textAlign = '';
        // if style is empty remove it
        if (!element.style.length) {
            element.removeAttribute('style');
        }
    }


}

const froalaClassnameReplacingIndent = (element, increase) => {
    let marginLeft = element.style.marginLeft.slice(0, -2);
    let sizeLetter = '';
    if(element.classList.length) {
        Array.from(element.classList).forEach(className => {
            if (className.includes('indent_')) {
                // size letter starts from seven letter of the class name
                sizeLetter = className.slice(7);
                // increase size letter by one
                if(increase){
                    sizeLetter = convertPxToSize(parseInt(convertSizeToPx(sizeLetter)) + 20);
                } else {
                    sizeLetter = convertPxToSize(parseInt(convertSizeToPx(sizeLetter)) - 20);
                }
                // remove old class
                element.classList.remove(className);
            } else {
                sizeLetter = convertPxToSize(parseInt(marginLeft));
            }
        });
    } else {
        sizeLetter = convertPxToSize(parseInt(marginLeft));
    }
    // add the new class
    element.classList.add(`indent_${sizeLetter}`);
    //if style is empty remove it
    element.style.marginLeft = '';
    if (!element.style.length) {
        element.removeAttribute('style');
    }
}

/*
 * Scroll event listener to hide the Froala Editor toolbar on scroll
 * when the editor is not focused.
 */
function hideToolbar() {
    const popup = document.querySelector('.editor-popup');
    if(popup){
        popup.addEventListener('scroll', (e) => {
            const toolbar = document.querySelectorAll('.fr-toolbar');
            if(toolbar.length){
                toolbar.forEach((el) => {
                   // clear top, right, bottom and left position
                    el.style.top = '';
                    el.style.right = '';
                    el.style.bottom = '';
                    el.style.left = '';
                    el.style.display = 'none';
                });
            }
        });
    }
}

const replaceQuotes = (e, that) => {
    const standardDoubleQuote = '"';
    const smartDoubleQuoteOpen = '“';
    const smartDoubleQuoteClose = '”';
    const standardSingleQuote = "'";
    const smartSingleQuoteOpen = '‘';
    const smartSingleQuoteClose = '’';

    const isDoubleQuote = e.key === standardDoubleQuote;

    if (e.key === standardDoubleQuote || e.key === standardSingleQuote) {
        e.preventDefault();

        const selection = that.selection.ranges(0);
        if (selection.startContainer.nodeType === Node.TEXT_NODE) {
            const text = selection.startContainer.textContent;
            const caretPosition = selection.startOffset;

            const openQuote = isDoubleQuote ? smartDoubleQuoteOpen : smartSingleQuoteOpen;
            const closeQuote = isDoubleQuote ? smartDoubleQuoteClose : smartSingleQuoteClose;

            // Updated condition to handle space before the single quote correctly
            const isApostrophe = !isDoubleQuote && caretPosition > 0 && text.charAt(caretPosition - 1).match(/\w/);

            const lastOpenQuoteIndex = text.substring(0, caretPosition).lastIndexOf(openQuote);
            const lastCloseQuoteIndex = text.substring(0, caretPosition).lastIndexOf(closeQuote);

            if (isApostrophe) {
                that.html.insert(smartSingleQuoteClose);
            } else if (lastOpenQuoteIndex === -1 || lastCloseQuoteIndex > lastOpenQuoteIndex) {
                that.html.insert(openQuote);
            } else {
                that.html.insert(closeQuote);
            }
        } else {
            that.html.insert(isDoubleQuote ? smartDoubleQuoteOpen : smartSingleQuoteOpen);
        }
    }
}

// const replaceQuotes = (e, that) => {
//     const standardDoubleQuote = '"';
//     const smartDoubleQuoteOpen = '“';
//     const smartDoubleQuoteClose = '”';
//     const standardSingleQuote = "'";
//     const smartSingleQuoteOpen = '‘';
//     const smartSingleQuoteClose = '’';
//     // Check if the pressed key is a standard double or single quote
//     if (e.key === standardDoubleQuote || e.key === standardSingleQuote) {
//         e.preventDefault(); // Prevent the standard quote from being inserted
//
//         const isDoubleQuote = e.key === standardDoubleQuote;
//         const openQuote = isDoubleQuote ? smartDoubleQuoteOpen : smartSingleQuoteOpen;
//         const closeQuote = isDoubleQuote ? smartDoubleQuoteClose : smartSingleQuoteClose;
//
//         const selection = that.selection.ranges(0);
//
//         if (selection.startContainer.nodeType === Node.TEXT_NODE) {
//             const text = selection.startContainer.textContent;
//             const lastQuoteIndex = text.lastIndexOf(openQuote);
//
//             if (lastQuoteIndex === -1 || text.substring(lastQuoteIndex).includes(closeQuote)) {
//                 that.html.insert(openQuote);
//             } else {
//                 that.html.insert(closeQuote);
//             }
//         } else {
//             that.html.insert(openQuote);
//         }
//     }
// }

const replaceQuotesOnPaste = (clipboardHtml) => {
    // const standardDoubleQuote = '"';
    // const smartDoubleQuoteOpen = '“';
    // const smartDoubleQuoteClose = '”';
    // const standardSingleQuote = "'";
    // const smartSingleQuoteOpen = '‘';
    // const smartSingleQuoteClose = '’';
    //
    // let doubleQuoteCount = 0;
    // let singleQuoteCount = 0;
    //
    // const result = [];
    // for (let i = 0; i < clipboardHtml.length; i++) {
    //     const currentChar = clipboardHtml.charAt(i);
    //     let replacement = currentChar;
    //
    //     if (currentChar === standardDoubleQuote) {
    //         replacement = doubleQuoteCount % 2 === 0 ? smartDoubleQuoteOpen : smartDoubleQuoteClose;
    //         doubleQuoteCount++;
    //     } else if (currentChar === standardSingleQuote) {
    //         replacement = singleQuoteCount % 2 === 0 ? smartSingleQuoteOpen : smartSingleQuoteClose;
    //         singleQuoteCount++;
    //     }
    //
    //     result.push(replacement);
    // }
    //
    // return result.join('');
};

function convertPxToSize(px) {
    if (px < 20) {
        return "1";
    } else if (px >= 30 * 20) {
        return "30";
    } else {
        const size = Math.floor(px / 20) + 1;
        return size.toString();
    }
}

function convertSizeToPx(size) {
    const sizes = {};
    for (let i = 1; i <= 30; i++) {
        sizes[i.toString()] = (i - 1) * 20;
    }
    return sizes[size];
}

function hideSettingsSidebar() {
    const sidebar = document.querySelector('.editor-settings-sidebar');
    if (sidebar) {
        sidebar.classList.remove('show');
    }
}
